import React from 'react';

import {ItemWithChildrenStructure} from 'app/general/structures/ItemWithChildrenStructure';
import ThreeDotsDropdownMenu from 'app/general/widgets/ThreeDotsDropdownMenu';
import {useDeleteArticle} from 'app/kbs/api';
import {getContentType} from 'app/utils/content-type';
import {gt} from 'app/utils/i18n';

import {KBArticleFeedbackChart} from './KBArticleFeedbackChart';
import {goToArticleDocEditURL} from './utils';

import {useDeleteModal} from '../KBTreeDeleteItemModal';
import {KBTreeItemInnerDiv} from '../KBTreeStructures';

import type {MenuItemsType} from 'app/general/widgets/ThreeDotsDropdownMenu';
import type {
  KBArticleSerializedType,
  KnowledgebaseSerializedType,
} from 'sweet-types';

type Props = {
  kb: KnowledgebaseSerializedType;
  article: KBArticleSerializedType;
};
export const KBTreeArticleItem = ({kb, article}: Props) => {
  const setDeleting = useDeleteModal();
  const ct = getContentType(article.document_content_type);

  const [deleteArticle] = useDeleteArticle();

  const doDeleteArticle = (payload: KBArticleSerializedType) => {
    // A promise is needed to close the modal; so I had to wrap
    // RTK mutation with a function to avoid unwrapping in the child component.
    return deleteArticle(payload).unwrap();
  };

  function actionsGetter(): MenuItemsType {
    return [
      {
        text: gt('Open article in Knowledge Base'),
        key: 'read-article',
        icon: 'far fa-eye',
        condition: () => true,
        callback: () =>
          new Promise((resolve) =>
            resolve(((window as Window).location = article.html_url))
          ),
      },
      {
        text: gt('Edit article document'),
        key: 'edit-article',
        icon: 'fa fa-pen',
        condition: () => true,
        callback: () => goToArticleDocEditURL(article),
      },
      {
        text: gt('Remove'),
        icon: 'far fa-trash',
        key: 'remove-article',
        condition: () => true,
        callback: () =>
          setDeleting({
            object: article,
            action: doDeleteArticle,
            description: gt(
              'Remove %(name)s %(contentType)s from this Knowledge Base?',
              {
                name: <b>{article.name}</b>,
                contentType: article.document_content_type,
              }
            ),
            title: gt('Remove'),
          }),
      },
    ];
  }

  return (
    <ItemWithChildrenStructure
      actionsChildren={
        <ThreeDotsDropdownMenu object={article} actionsGetter={actionsGetter} />
      }
    >
      <KBTreeItemInnerDiv itemIcon={<i className={ct.icon} />}>
        <div className="flex-grow-1">{article.name}</div>
        {kb.feedback ? <KBArticleFeedbackChart article={article} /> : null}
      </KBTreeItemInnerDiv>
    </ItemWithChildrenStructure>
  );
};
